import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Sun, Moon, SunMoon, AlertTriangle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { getSessions, getMonths, deleteSessions, eur, kwh } from "@/lib/api";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function TariffBadge({ type }) {
  const map = {
    Dag: { icon: Sun, cls: "bg-day/15 text-day border-day/30" },
    Nacht: { icon: Moon, cls: "bg-night/15 text-night border-night/30" },
    Combinatie: { icon: SunMoon, cls: "bg-muted text-muted-foreground border-border" },
    "Geen tarief": { icon: AlertTriangle, cls: "bg-destructive/10 text-destructive border-destructive/30" },
  };
  const { icon: Icon, cls } = map[type] || map.Combinatie;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-xs font-medium ${cls}`}>
      <Icon className="h-3 w-3" /> {type}
    </span>
  );
}

export default function Sessions() {
  const qc = useQueryClient();
  const { data: months } = useQuery({ queryKey: ["months"], queryFn: getMonths });
  const [month, setMonth] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [confirm, setConfirm] = useState(null); // { ids: [] } pending delete

  useEffect(() => {
    if (months && months.length && !month) setMonth(months[0]);
  }, [months, month]);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["sessions", month],
    queryFn: () => getSessions(month),
    enabled: !!month,
  });

  useEffect(() => { setSelected(new Set()); }, [month]);

  const delMut = useMutation({
    mutationFn: (ids) => deleteSessions(ids),
    onSuccess: (d) => {
      toast.success(`${d.deleted} laadsessie${d.deleted === 1 ? "" : "s"} verwijderd`);
      setSelected(new Set());
      setConfirm(null);
      qc.invalidateQueries();
    },
    onError: () => { toast.error("Verwijderen mislukt"); setConfirm(null); },
  });

  const allSelected = rows.length > 0 && selected.size === rows.length;
  const someSelected = selected.size > 0 && !allSelected;

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.id)));
  };
  const toggleOne = (id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const fmtDur = (m) => `${Math.floor(m / 60)}u ${m % 60}m`;
  const confirmCount = confirm?.ids?.length || 0;

  return (
    <div>
      <PageHeader subtitle="Detail" title="Laadsessies" month={month} setMonth={setMonth} />

      {selected.size > 0 && (
        <div className="flex items-center justify-between gap-4 border border-border bg-card rounded-lg px-4 py-3 mb-4" data-testid="selection-bar">
          <span className="text-sm font-medium">{selected.size} geselecteerd</span>
          <Button variant="destructive" size="sm" className="gap-2"
            onClick={() => setConfirm({ ids: [...selected] })} data-testid="delete-selected-btn">
            <Trash2 className="h-4 w-4" /> Verwijder geselecteerde
          </Button>
        </div>
      )}

      <div className="border border-border rounded-lg bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50 hover:bg-muted/50">
                <TableHead className="w-10">
                  <Checkbox checked={allSelected ? true : (someSelected ? "indeterminate" : false)}
                    onCheckedChange={toggleAll} data-testid="select-all-checkbox" aria-label="Alles selecteren" />
                </TableHead>
                <TableHead>Datum</TableHead>
                <TableHead>Van</TableHead>
                <TableHead>Tot</TableHead>
                <TableHead>Duur</TableHead>
                <TableHead className="text-right">kWh</TableHead>
                <TableHead className="text-right">Dag</TableHead>
                <TableHead className="text-right">Nacht</TableHead>
                <TableHead>Tarief</TableHead>
                <TableHead className="text-right">Kost</TableHead>
                <TableHead>Bedrijf</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow><TableCell colSpan={12} className="text-center py-10 text-muted-foreground">Laden…</TableCell></TableRow>
              )}
              {!isLoading && rows.length === 0 && (
                <TableRow><TableCell colSpan={12} className="text-center py-10 text-muted-foreground">Geen laadsessies in deze maand.</TableCell></TableRow>
              )}
              {rows.map((r, i) => {
                const isSel = selected.has(r.id);
                return (
                  <TableRow key={r.id} data-state={isSel ? "selected" : undefined}
                    className="row-in" style={{ animationDelay: `${Math.min(i * 20, 400)}ms` }} data-testid={`session-row-${r.id}`}>
                    <TableCell>
                      <Checkbox checked={isSel} onCheckedChange={() => toggleOne(r.id)}
                        data-testid={`session-checkbox-${r.id}`} aria-label="Selecteer sessie" />
                    </TableCell>
                    <TableCell className="font-mono text-sm">{r.date}</TableCell>
                    <TableCell className="font-mono text-sm">{r.start}</TableCell>
                    <TableCell className="font-mono text-sm">{r.stop}</TableCell>
                    <TableCell className="font-mono text-sm text-muted-foreground">{fmtDur(r.duration_min)}</TableCell>
                    <TableCell className="text-right font-mono font-semibold">{kwh(r.total_kwh)}</TableCell>
                    <TableCell className="text-right font-mono text-day">{r.day_kwh == null ? "—" : kwh(r.day_kwh)}</TableCell>
                    <TableCell className="text-right font-mono text-night">{r.night_kwh == null ? "—" : kwh(r.night_kwh)}</TableCell>
                    <TableCell><TariffBadge type={r.tariff_type} /></TableCell>
                    <TableCell className="text-right font-mono font-semibold">{r.cost == null ? "—" : eur(r.cost)}</TableCell>
                    <TableCell className="text-sm max-w-[180px] truncate">{r.company_name}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={() => setConfirm({ ids: [r.id] })} data-testid={`session-delete-${r.id}`} aria-label="Verwijder sessie">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmCount === 1 ? "Laadsessie verwijderen?" : `${confirmCount} laadsessies verwijderen?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              De geselecteerde laadsessie{confirmCount === 1 ? "" : "s"} word{confirmCount === 1 ? "t" : "en"} verwijderd
              en niet langer meegeteld in het overzicht en de onkostenvergoedingen. Deze actie kan niet ongedaan gemaakt worden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Annuleren</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => delMut.mutate(confirm.ids)}
              disabled={delMut.isPending}
              data-testid="delete-confirm-btn"
            >
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
